import React, { Component } from 'react';
import { Modal } from 'antd';
import _EnterpriseOpsService from 'modules/EnterpriseOpsService';
import _notification from 'modules/notification';
import Button from 'components/controls/Button';
import TextArea from 'components/controls/TextArea';
import PageHeader from 'components/enterprise/PageHeader';
import StatusBadge from 'components/enterprise/StatusBadge';
import LoadingSpinner from 'components/enterprise/LoadingSpinner';
import EmptyState from 'components/enterprise/EmptyState';
import moment from 'moment';
import { routesAdmin } from 'routes/Routes';

// Sprint 2.4 (Governance Experience): consumes Sprint 2.2's maker-checker APIs, which had zero
// frontend surface before this sprint. The existing direct ResetData/PortalConfiguration/
// PostConfiguration endpoints are unaffected — this page only lists/approves/rejects requests that
// were already submitted via GovernanceController.SubmitForApproval.
class GovernanceRequests extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            pendingRequests: [],
            auditTimeline: [],
            detailsModalVisible: false,
            selectedRequest: null,
            rejectModalVisible: false,
            rejectComments: '',
            actioningId: null
        };
    }

    componentDidMount() {
        this.loadPendingRequests();
        this.loadAuditTimeline();
    }

    loadPendingRequests = async () => {
        this.setState({ loading: true });
        try {
            const response = await _EnterpriseOpsService.getPendingGovernanceRequests();
            this.setState({ pendingRequests: response.data || [], loading: false });
        } catch (e) {
            this.setState({ loading: false });
        }
    };

    loadAuditTimeline = async () => {
        try {
            const response = await _EnterpriseOpsService.searchAuditLog('ApprovalDecision', null, null, null, 20);
            this.setState({ auditTimeline: response.data || [] });
        } catch (e) { }
    };

    handleViewDetails = (request) => {
        this.setState({ selectedRequest: request, detailsModalVisible: true });
    };

    handleApprove = async (requestId) => {
        this.setState({ actioningId: requestId });
        try {
            const response = await _EnterpriseOpsService.approveGovernanceRequest(requestId);
            if (response.data && response.data.IsSuccess) {
                _notification.showMessage('success', response.data.Message || 'Approved successfully.');
                this.loadPendingRequests();
                this.loadAuditTimeline();
            } else {
                _notification.showMessage('error', (response.data && response.data.Message) || 'Approval failed.');
            }
        } catch (e) {
            _notification.showMessage('error', 'Something went wrong.');
        }
        this.setState({ actioningId: null });
    };

    openRejectModal = (request) => {
        this.setState({ selectedRequest: request, rejectModalVisible: true, rejectComments: '' });
    };

    handleReject = async () => {
        const { selectedRequest, rejectComments } = this.state;
        if (!selectedRequest) return;

        this.setState({ actioningId: selectedRequest.Id });
        try {
            const response = await _EnterpriseOpsService.rejectGovernanceRequest(selectedRequest.Id, rejectComments);
            if (response.data && response.data.IsSuccess) {
                _notification.showMessage('success', response.data.Message || 'Rejected.');
                this.setState({ rejectModalVisible: false, selectedRequest: null });
                this.loadPendingRequests();
                this.loadAuditTimeline();
            } else {
                _notification.showMessage('error', (response.data && response.data.Message) || 'Rejection failed.');
            }
        } catch (e) {
            _notification.showMessage('error', 'Something went wrong.');
        }
        this.setState({ actioningId: null });
    };

    formatPayload = (payloadJson) => {
        try {
            return JSON.stringify(JSON.parse(payloadJson), null, 2);
        } catch (e) {
            return payloadJson;
        }
    };

    render() {
        const { loading, pendingRequests, auditTimeline, detailsModalVisible, selectedRequest, rejectModalVisible, rejectComments, actioningId } = this.state;

        return (
            <div className="ent-admin-page">
                <PageHeader
                    title="Governance"
                    subtitle="Review and decide on pending maker-checker requests for Recruitment Cycle Reset, Portal Configuration, and Post Configuration changes."
                    breadcrumbItems={[{ label: 'Dashboard', path: routesAdmin.dashboard.path }, { label: 'Governance' }]}
                />

                <div className="ent-section">
                    <div className="ent-section-title">Pending Requests</div>
                    {loading ? <LoadingSpinner tip="Loading pending requests..." /> : (
                        pendingRequests.length === 0 ? (
                            <EmptyState icon="check-circle" title="No pending requests" description="There is nothing awaiting approval right now." />
                        ) : (
                            <table className="ent-table">
                                <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th>Requested By (Role)</th>
                                        <th>Requested At</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingRequests.map(req => (
                                        <tr key={req.Id}>
                                            <td>{req.RequestType}</td>
                                            <td>{req.RequestedByRole} (#{req.RequestedByUserId})</td>
                                            <td>{moment(req.RequestedAt).format('DD-MMM-YYYY HH:mm')}</td>
                                            <td><StatusBadge status={req.Status} /></td>
                                            <td>
                                                <Button
                                                    type="button"
                                                    value="View"
                                                    className="button-global button-border autoWidth mr20"
                                                    onClick={() => this.handleViewDetails(req)}
                                                />
                                                <Button
                                                    type="button"
                                                    value={actioningId === req.Id ? 'Working...' : 'Approve'}
                                                    disabled={actioningId === req.Id}
                                                    className="button-global button-primary autoWidth mr20"
                                                    onClick={() => this.handleApprove(req.Id)}
                                                />
                                                <Button
                                                    type="button"
                                                    value="Reject"
                                                    disabled={actioningId === req.Id}
                                                    className="button-global removebutton button-border autoWidth"
                                                    onClick={() => this.openRejectModal(req)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )
                    )}
                </div>

                <div className="ent-section">
                    <div className="ent-section-title">Audit Timeline</div>
                    {auditTimeline.length === 0 ? (
                        <EmptyState icon="history" title="No governance activity yet" />
                    ) : (
                        <table className="ent-table">
                            <thead>
                                <tr>
                                    <th>Event</th>
                                    <th>Entity</th>
                                    <th>Details</th>
                                    <th>When</th>
                                </tr>
                            </thead>
                            <tbody>
                                {auditTimeline.map((row, idx) => (
                                    <tr key={idx}>
                                        <td>{row.EventType}</td>
                                        <td>{row.EntityType} {row.EntityId ? '#' + row.EntityId : ''}</td>
                                        <td>{row.Details}</td>
                                        <td>{moment(row.Timestamp).format('DD-MMM-YYYY HH:mm')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <Modal
                    title="Request Details"
                    visible={detailsModalVisible}
                    onCancel={() => this.setState({ detailsModalVisible: false })}
                    footer={null}
                    width={600}
                >
                    {selectedRequest && (
                        <div>
                            <p><strong>Type:</strong> {selectedRequest.RequestType}</p>
                            <p><strong>Requested By:</strong> {selectedRequest.RequestedByRole} (#{selectedRequest.RequestedByUserId})</p>
                            <p><strong>Requested At:</strong> {moment(selectedRequest.RequestedAt).format('DD-MMM-YYYY HH:mm')}</p>
                            <p><strong>Proposed Change:</strong></p>
                            <pre style={{ background: 'var(--admin-bg-over-color)', padding: 'var(--spacing-sm)', borderRadius: 'var(--admin-border-radius)', maxHeight: 300, overflow: 'auto' }}>
                                {this.formatPayload(selectedRequest.PayloadJson)}
                            </pre>
                        </div>
                    )}
                </Modal>

                <Modal
                    title="Reject Request"
                    visible={rejectModalVisible}
                    onCancel={() => this.setState({ rejectModalVisible: false })}
                    onOk={this.handleReject}
                    okText="Reject"
                >
                    <TextArea
                        name="rejectComments"
                        heading="Comments (optional)"
                        value={rejectComments}
                        onChange={(e) => this.setState({ rejectComments: e.target.value })}
                    />
                </Modal>
            </div>
        );
    }
}

export default GovernanceRequests;
